<template>
    <UModal :model-value="open" @update:model-value="$emit('update:open', $event)" :ui="{ width: 'w-full sm:max-w-md' }">
        <UForm ref="form" @submit="onSubmit" :schema="schema" :state="state" :validate-on="['submit']" class="h-full">
            <UCard :ui="{ rounded: 'rounded-lg', body: { base: 'overflow-visible' } }">
                <template #header>
                    <div class="flex items-center justify-between">
                        <h3 class="text-base font-medium leading-6 text-gray-800">Edit Family Name</h3>
                        <UButton color="gray" variant="ghost" icon="i-ph-x-bold" class="-my-1" @click="dismiss()" />
                    </div>
                </template>
                <UFormGroup name="name" label="Name" required>
                    <UInput v-model="state.name" type="text" size="lg" autofocus />
                </UFormGroup>
                <template #footer>
                    <div class="flex items-center justify-end space-x-2">
                        <UButton @click="dismiss()" size="lg" color="white"> Cancel </UButton>
                        <UButton :loading="loading" type="submit" size="lg" color="black"> Save </UButton>
                    </div>
                </template>
            </UCard>
        </UForm>
    </UModal>
</template>

<script setup lang="ts">
import { z } from 'zod'
import type { FormSubmitEvent } from '#ui/types'
import type { Family } from '~/types/family'

const emit = defineEmits<{
    'update:open': [boolean]
    'reload': [void]
}>()

const props = defineProps<{
    open: boolean
    family?: Family
}>()

const client = useSupabaseClient()
const toast = useToast()

const form = ref()
const loading = ref<boolean>(false)

const schema = z.object({
    name: z.string().trim().min(1, 'Name is required'),
})

const state = ref<z.infer<typeof schema>>({ name: '' })

watch(() => props.open, (open) => {
    if (open) {
        state.value = { name: props.family?.name || '' }
    }
})

async function onSubmit(event: FormSubmitEvent<z.infer<typeof schema>>) {
    if (!props.family?.id) return

    loading.value = true

    const { error } = await client
        .from('families')
        .update({ name: event.data.name.trim() } as never)
        .eq('id', props.family.id)

    loading.value = false

    if (error) {
        toast.add({
            title: 'An error has occurred, try again later.',
            color: 'red',
            icon: 'i-ph-x-circle-duotone',
        })
        dismiss()
    } else {
        toast.add({
            title: 'Family name updated!',
            color: 'green',
            icon: 'i-ph-check-circle-duotone',
        })
        dismiss(true)
    }
}

function dismiss(reload: boolean = false) {
    emit('update:open', false)

    if (reload) {
        emit('reload')
    }
}
</script>
